<template>
  <div class="relative" ref="dropdownRef">
    <button
      :disabled="!isEnabled"
      @click="toggleDropdown"
      class="cursor-pointer rounded-sm p-2 relative hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent"
      aria-label="Open notifications"
    >
      <BellIcon />
      <span
        v-if="unreadCount > 0"
        class="absolute -right-1 top-0 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] text-white"
      >
        {{ unreadCount > 99 ? "99+" : unreadCount }}
      </span>
    </button>

    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-1"
    >
      <div
        v-show="isOpen"
        class="notif-panel fixed inset-x-4 top-18 z-50 flex max-h-[min(480px,calc(100dvh-6rem))] flex-col rounded-lg border border-gray-200 bg-white shadow-xl shadow-black/8 sm:absolute sm:inset-x-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-[400px] sm:max-h-[480px]"
      >
        <!-- Header -->
        <div class="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h3 class="text-[17px] font-semibold tracking-[-0.01em] text-gray-900">
            Latest Notifications
          </h3>
          <button
            v-if="hasNotifications && unreadCount > 0"
            class="text-h5 font-medium text-lightBlue transition-colors hover:text-lightBlue/80"
            @click="handleMarkAllRead"
          >
            Mark all as Read
          </button>
        </div>

        <!-- Empty / disabled states -->
        <div v-if="!isEnabled" class="px-5 py-10 text-center">
          <div class="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-50">
            <svg class="h-5 w-5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
            </svg>
          </div>
          <p class="text-h5 font-medium text-gray-400">Notifications are unavailable here.</p>
          <p class="mt-0.5 text-h6 text-gray-300">This area does not have a notification inbox.</p>
        </div>

        <div v-else-if="!hasNotifications && isReady" class="px-5 py-10 text-center">
          <div v-if="loadError" class="px-1">
            <div class="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-500">
              <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zM12 15.75h.008v.008H12v-.008z" />
              </svg>
            </div>
            <p class="text-h5 font-medium text-gray-500">Notifications could not be loaded.</p>
            <p class="mt-0.5 text-h6 text-gray-400">{{ loadError }}</p>
            <button
              type="button"
              class="mt-4 inline-flex cursor-pointer items-center justify-center rounded-md border border-gray-200 px-3 py-2 text-h5 font-medium text-gray-700 transition-colors hover:bg-gray-50"
              @click="handleRetry"
            >
              Retry
            </button>
          </div>
          <template v-else>
          <div class="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-50">
            <svg class="h-5 w-5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
            </svg>
          </div>
          <p class="text-h5 font-medium text-gray-400">No notifications yet.</p>
          <p class="mt-0.5 text-h6 text-gray-300">New project and chat activity will show up here.</p>
          </template>
        </div>

        <!-- Notification list -->
        <div v-else class="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain p-4 gap-1">
          <div
            v-for="notification in notifications"
            :key="notification.recipient_id"
            class="group"
          >
            <a
              :href="getNotificationDeepLink(notification)"
              class="block w-full rounded-lg text-left transition-colors duration-150 cursor-pointer"
              :class="notification.is_read
                ? 'hover:bg-se-gray-100'
                : 'border border-se-gray-200 bg-se-gray-100 hover:border-se-gray-500'"
              @click="handleNotificationOpen($event, notification)"
            >
              <NotificationPreviewCard :notification="notification">
                <template #trailing-action>
                  <UiTooltip :content="notification.is_read ? 'Mark as unread' : 'Mark as read'">
                    <button
                      type="button"
                      class="inline-flex cursor-pointer items-center justify-center text-gray-400 opacity-100 transition-all duration-150 hover:text-gray-700 focus:opacity-100 focus:outline-none sm:opacity-0 sm:group-hover:opacity-100"
                      :aria-label="notification.is_read ? 'Mark notification as unread' : 'Mark notification as read'"
                      @click.stop.prevent="handleToggleRead(notification)"
                    >
                      <span
                        v-if="notification.is_read"
                        class="block h-2.5 w-2.5 rounded-full bg-current"
                        aria-hidden="true"
                      />
                      <UiIcon
                        v-else
                        name="check"
                        class="h-4 w-4"
                      />
                    </button>
                  </UiTooltip>
                </template>
              </NotificationPreviewCard>
            </a>
          </div>

          <div ref="loadMoreRef" class="h-4 w-full shrink-0" />
          <p v-if="isLoadingMore" class="pb-3 text-center text-h6 text-gray-400">
            Loading more...
          </p>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { onClickOutside, useIntersectionObserver } from "@vueuse/core";
import { useRouter } from "vue-router";
import { UiIcon, UiTooltip, useToaster } from "@shopexperts/shared";
import BellIcon from "@/assets/icons/bell.svg";
import NotificationPreviewCard from "@/v2Projects/components/NotificationPreviewCard.vue";
import { useNotifications } from "@/v2Projects/composables/useNotifications";
import { buildProjectUserError } from "@/v2Projects/userErrors";
import type { DashboardNotification } from "@/v2Projects/types";
import { getNotificationDeepLink } from "@/v2Projects/utils/notificationPresentation";

const router = useRouter();
const toaster = useToaster();
const dropdownRef = ref<HTMLElement | null>(null);
const loadMoreRef = ref<HTMLElement | null>(null);
const isOpen = ref(false);

const {
  notifications,
  unreadCount,
  loadError,
  isReady,
  isLoadingMore,
  hasMore,
  isEnabled,
  loadMore,
  markRead,
  markUnread,
  markAllRead,
  refresh,
} = useNotifications();

const hasNotifications = computed(() => notifications.value.length > 0);

function toggleDropdown() {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    refresh();
  }
}

function isPlainLeftClick(event: MouseEvent) {
  return event.button === 0
    && !event.metaKey
    && !event.ctrlKey
    && !event.shiftKey
    && !event.altKey
    && !event.defaultPrevented;
}

async function handleNotificationOpen(
  event: MouseEvent,
  notification: DashboardNotification,
) {
  isOpen.value = false;

  if (!isPlainLeftClick(event)) {
    if (!notification.is_read) {
      void markRead(notification.recipient_id).catch(showNotificationUpdateError);
    }
    return;
  }

  event.preventDefault();

  if (!notification.is_read) {
    try {
      await markRead(notification.recipient_id);
    } catch (error) {
      showNotificationUpdateError(error);
      return;
    }
  }
  await router.push(getNotificationDeepLink(notification));
}

async function handleMarkAllRead() {
  try {
    await markAllRead();
  } catch (error) {
    showNotificationUpdateError(error);
  }
}

function handleRetry() {
  refresh();
}

async function handleToggleRead(notification: DashboardNotification) {
  try {
    if (notification.is_read) {
      await markUnread(notification.recipient_id);
      return;
    }

    await markRead(notification.recipient_id);
  } catch (error) {
    showNotificationUpdateError(error);
  }
}

function showNotificationUpdateError(error: unknown) {
  const message = buildProjectUserError("updateNotification", error);
  toaster.create({
    title: message.title,
    description: loadError.value ?? message.description,
    type: "error",
  });
}

useIntersectionObserver(loadMoreRef, ([entry]) => {
  if (entry?.isIntersecting && isOpen.value && hasMore.value) {
    loadMore();
  }
});

onClickOutside(dropdownRef, () => {
  isOpen.value = false;
});
</script>
